/**
 * Aceite do convite: `/convite?token=…`.
 *
 * É aqui que a senha nasce, e quem convidou nunca a conhece. A tela mostra
 * para quem é o convite antes de pedir a senha — chegar num formulário de
 * senha sem saber de qual conta se trata é desconfortável e leva a abandono.
 *
 * Convite inválido, expirado ou já usado dão a MESMA mensagem: distinguir
 * confirmaria que o token existiu, e esta página é aberta a qualquer um.
 */
import { SENHA_MAX, SENHA_MIN, mensagemSenhaInvalida } from '@simpleadm/shared-types';
import { CheckCircle2, UserPlus } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { CampoSenha } from '@/components/CampoSenha';
import { MolduraAcesso } from '@/components/MolduraAcesso';
import { RequisitosSenha } from '@/components/RequisitosSenha';
import { ApiError, apiFetch } from '@/lib/api';

interface Convite {
  fullName: string;
  email: string;
  role: string;
  expiresAt: string;
}

const PAPEL: Record<string, string> = {
  SUPER_ADMIN: 'Administrador geral',
  ADMIN: 'Administrador',
  OPERATOR: 'Operador',
};

function mensagemDaApi(e: ApiError): string | null {
  try {
    const p = JSON.parse(e.body) as { message?: string | string[] };
    const m = p.message;
    return Array.isArray(m) ? (m[0] ?? null) : (m ?? null);
  } catch {
    return null;
  }
}

export function ConvitePage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [convite, setConvite] = useState<Convite | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erroConvite, setErroConvite] = useState<string | null>(null);

  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [feito, setFeito] = useState(false);

  useEffect(() => {
    if (!token) {
      setCarregando(false);
      setErroConvite('Este endereço não tem o código do convite. Abra o link como veio no e-mail.');
      return;
    }
    let ativo = true;
    apiFetch<Convite>(`/auth/invite/${encodeURIComponent(token)}`, { skipAuth: true })
      .then((c) => {
        if (ativo) setConvite(c);
      })
      .catch((e) => {
        if (!ativo) return;
        setErroConvite(
          e instanceof ApiError
            ? (mensagemDaApi(e) ?? 'Convite inválido ou expirado.')
            : 'Não foi possível abrir o convite agora.',
        );
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [token]);

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    setErro(null);
    const problema = mensagemSenhaInvalida(senha);
    if (problema) return setErro(problema);
    if (senha !== confirmacao) return setErro('A confirmação não bate com a senha.');
    setEnviando(true);
    try {
      await apiFetch<void>('/auth/accept-invite', {
        method: 'POST',
        body: JSON.stringify({ token, password: senha }),
        skipAuth: true,
      });
      setFeito(true);
    } catch (e) {
      setErro(
        e instanceof ApiError
          ? (mensagemDaApi(e) ?? 'Não foi possível criar a senha.')
          : 'Não foi possível criar a senha agora.',
      );
    } finally {
      setEnviando(false);
    }
  }

  return (
    <MolduraAcesso titulo="Criar seu acesso" icone={<UserPlus className="h-5 w-5" />}>
      {carregando && <p className="text-sm text-muted-foreground">Abrindo convite…</p>}

      {!carregando && erroConvite && (
        <div className="space-y-3 text-sm">
          <p>{erroConvite}</p>
          <p className="text-xs text-muted-foreground">
            Convites valem por 7 dias. Peça um novo a quem criou seu acesso.
          </p>
          <Link to="/login" className="block text-center text-xs underline-offset-2 hover:underline">
            Ir para o login
          </Link>
        </div>
      )}

      {!carregando && convite && feito && (
        <div className="space-y-3 text-sm">
          <p className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" />
            <span>
              Acesso criado. Entre com <b>{convite.email}</b> e a senha que você acabou de definir.
            </span>
          </p>
          <Link
            to="/login"
            className="block w-full rounded-md bg-primary py-2.5 text-center text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Ir para o login
          </Link>
        </div>
      )}

      {!carregando && convite && !feito && (
        <form onSubmit={enviar} className="space-y-4">
          <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
            <p className="font-medium">{convite.fullName}</p>
            <p className="text-xs text-muted-foreground">{convite.email}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Perfil: {PAPEL[convite.role] ?? convite.role}
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            Defina a senha do seu acesso. Ninguém além de você vai conhecê-la.
          </p>

          <CampoSenha
            label="Sua senha"
            required
            minLength={SENHA_MIN}
            maxLength={SENHA_MAX}
            autoComplete="new-password"
            autoFocus
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            disabled={enviando}
            abaixo={<RequisitosSenha senha={senha} />}
          />
          <CampoSenha
            label="Repita a senha"
            required
            autoComplete="new-password"
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            disabled={enviando}
          />

          {erro && (
            <p role="alert" className="text-sm text-destructive">
              {erro}
            </p>
          )}

          <button
            type="submit"
            disabled={enviando}
            className="w-full rounded-md bg-primary py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {enviando ? 'Criando…' : 'Criar acesso'}
          </button>
        </form>
      )}
    </MolduraAcesso>
  );
}
